import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insidePages } from '../data/content';

/** Interior pages — swipe on touch, arrows and dots on desktop. */
export function InsidePagesCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = insidePages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    const slide = track.children[next] as HTMLElement | undefined;
    if (slide) track.scrollTo({
      left: slide.offsetLeft,
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const slides = Array.from(track.children) as HTMLElement[];
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      if (atEnd) {
        setActive(total - 1);
        return;
      }
      let closest = 0;
      slides.forEach((slide, index) => {
        if (Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[closest].offsetLeft - track.scrollLeft)) {
          closest = index;
        }
      });
      setActive(closest);
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, [total]);
  return <div className="relative mx-auto max-w-5xl" aria-roledescription="carousel" aria-label="Pages de Nail Educator Ready" data-id="element-18" data-mp="3jtmve4dzw48u">
      <div ref={trackRef} className="no-scrollbar relative flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2" data-id="element-19" data-mp="3tppvl4padst5">
        {insidePages.map((page, index) => <figure key={page.src} className="w-[86%] flex-none snap-start sm:w-[calc((100%-1rem)/2)] lg:w-[calc((100%-2rem)/3)]" aria-roledescription="slide" aria-label={`${index + 1} sur ${total} : ${page.caption}`} data-id="element-20" data-mp="1n03093yggx5" data-mpi={index}>
            <img src={page.src} alt={`${page.caption} — page intérieure de Nail Educator Ready`} width={720} height={900} loading={index < 2 ? 'eager' : 'lazy'} decoding="async" draggable={false} className="block aspect-[4/5] h-auto w-full select-none rounded-[18px] border border-brand-charcoal/10 bg-white object-cover shadow-[0_30px_60px_-36px_rgba(90,70,40,0.5)]" data-id="element-21" data-mp="3a05dgg0pxm0j" />
            <figcaption className="mt-4 px-1" data-id="element-22" data-mp="2md719ujmt08f">
              <span className="block font-display text-[1.25rem] font-bold leading-tight text-brand-charcoal" data-id="element-23" data-mp="303aho4vtxzpy">
                {page.label}
              </span>
              <span className="mt-1 block font-body text-[12px] text-brand-charcoal/55" data-id="element-24" data-mp="2vb0xl5hebtvl">{page.caption}</span>
            </figcaption>
          </figure>)}
      </div>

      <ArrowButton side="left" label="Page précédente" onClick={() => goTo(active - 1)} data-id="element-25" data-mp="22ooibup7yj26">
        <ChevronLeftIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-26" data-mp="i5cfxack107o" />
      </ArrowButton>
      <ArrowButton side="right" label="Page suivante" onClick={() => goTo(active + 1)} data-id="element-27" data-mp="mwudrpja6kof">
        <ChevronRightIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-28" data-mp="95plx9m5mwon" />
      </ArrowButton>

      <div className="mt-6 flex items-center justify-center gap-2.5" data-id="element-29" data-mp="e3hd21ez6ers">
        {insidePages.map((page, index) => <button key={page.src} type="button" aria-label={`Afficher : ${page.caption}`} aria-current={index === active} onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cta focus-visible:ring-offset-2 ${index === active ? 'w-8 bg-brand-cta' : 'w-2 bg-brand-charcoal/20 hover:bg-brand-charcoal/35'}`} data-id="element-30" data-mp="11uwgjxihn24m" data-mpi={index} />)}
      </div>
    </div>;
}
function ArrowButton({
  side,
  label,
  onClick,
  children
}: {
  side: 'left' | 'right';
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} aria-label={label} className={`absolute top-[calc(50%-4rem)] hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-brand-charcoal/10 bg-white text-brand-charcoal shadow-[0_10px_24px_-12px_rgba(42,37,32,0.4)] transition-[background-color,transform] duration-150 ease-out hover:bg-brand-ivory active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cta md:flex ${side === 'left' ? '-left-6' : '-right-6'}`} data-id="element-31" data-mp="3o2c66c223uvw">
      {children}
    </button>;
}